import { useEffect, useState } from 'react';
import { useForm } from '@tanstack/react-form';
import { Loader2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useApiMutation } from '@/hooks/use-api-query';
import { FieldError } from '@/components/form/field-error';
import { webhookUrlSchema } from '@/lib/form/schemas';
import { docsUrl } from '@/lib/docs-url';

export interface WebhookSubmitConfig {
  /** API endpoint path (relative to the fetch base URL). */
  endpoint: string;
  /** HTTP method — POST for create, PUT/PATCH for update. */
  method: 'POST' | 'PUT' | 'PATCH';
  /**
   * Field name to use in the request body for the verify-token value.
   * Default: 'verifyToken'.
   */
  verifyTokenField?: string;
  /** Extra fields to merge into the request body (e.g., `{ channelId }`). */
  extraBody?: Record<string, unknown>;
}

interface WebhookConfigDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfigured: (webhookUrl: string, verifyToken: string) => void;
  mode: 'configure' | 'edit';
  submitConfig: WebhookSubmitConfig;
  currentWebhookUrl?: string;
  currentVerifyToken?: string;
  /** Optional copy override for sandbox/channel-specific phrasing. */
  titleOverride?: string;
  descriptionOverride?: string;
  /** Optional helper text rendered under the verify-token input. */
  verifyTokenHelp?: string;
  /**
   * Fallback value used when `currentVerifyToken` is empty/undefined. Default
   * is `'test123'` (preserves the channels flow where users invent a token).
   * Sandbox callers pass `session.accessToken` so the user sees their familiar
   * bind code instead of an arbitrary placeholder.
   */
  fallbackVerifyToken?: string;
}

export function normalizeWebhookUrl(value: string): string {
  const trimmed = value.trim();
  if (trimmed && !/^https?:\/\//i.test(trimmed)) return `https://${trimmed}`;
  return trimmed;
}

function validateWebhookUrl(value: string): string | undefined {
  const parsed = webhookUrlSchema.safeParse(normalizeWebhookUrl(value));
  return parsed.success
    ? undefined
    : (parsed.error.issues[0]?.message ?? 'Enter a valid URL');
}

export function WebhookConfigDialog({
  open,
  onOpenChange,
  onConfigured,
  mode,
  submitConfig,
  currentWebhookUrl,
  currentVerifyToken,
  titleOverride,
  descriptionOverride,
  verifyTokenHelp,
  fallbackVerifyToken = 'test123',
}: WebhookConfigDialogProps) {
  const [verifyToken, setVerifyToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const form = useForm({
    defaultValues: { webhookUrl: '' },
    onSubmit: ({ value }) => {
      handleSubmit(value.webhookUrl);
    },
  });

  // The save mutation owns the wire call; throwForResponse routes the server's
  // message through the parseApiError chokepoint (scrub + analytics), with the
  // same action-specific fallback copy as before.
  const saveWebhook = useApiMutation<unknown, Record<string, unknown>>((body) => ({
    path: submitConfig.endpoint,
    init: {
      method: submitConfig.method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
    fallback: 'Failed to save webhook URL. Please try again.',
  }));
  const verifying = saveWebhook.isPending;

  /* eslint-disable react-hooks/set-state-in-effect -- modal-session reset; must run on open/close transitions, not during render (same pattern as edit-profile-dialog) */
  useEffect(() => {
    if (open) {
      const seedUrl = mode === 'edit' && currentWebhookUrl ? currentWebhookUrl : '';
      // Audit R4-7 (AIT-111): edit opened with an empty URL field even though
      // the channel has one. form.reset(values) clears the field but doesn't
      // reliably seed the mounted Field's subscription; setFieldValue does.
      form.reset({ webhookUrl: seedUrl });
      form.setFieldValue('webhookUrl', seedUrl);
      // `||` not `??`: the sandbox DTO mapper coalesces a missing wire field
      // to '' — an empty currentVerifyToken must still yield the fallback.
      setVerifyToken(currentVerifyToken || fallbackVerifyToken);
      setError(null);
    }
  }, [form, open, mode, currentWebhookUrl, currentVerifyToken, fallbackVerifyToken]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const handleSubmit = (webhookUrl: string) => {
    setError(null);
    const normalizedUrl = normalizeWebhookUrl(webhookUrl);
    form.setFieldValue('webhookUrl', normalizedUrl);

    const verifyTokenField = submitConfig.verifyTokenField ?? 'verifyToken';
    const body: Record<string, unknown> = {
      ...(submitConfig.extraBody ?? {}),
      webhookUrl: normalizedUrl,
      [verifyTokenField]: verifyToken.trim(),
    };

    saveWebhook.mutate(body, {
      onSuccess: () => {
        toast.success('Webhook URL verified');
        onConfigured(normalizedUrl, verifyToken.trim());
      },
      onError: (err) =>
        setError(err.userMessage ?? 'Failed to save webhook URL. Please try again.'),
    });
  };

  const ctaText = 'Verify & Save';
  const dialogTitle =
    titleOverride ??
    (mode === 'edit' ? 'Update Webhook' : 'Configure Webhook');
  const dialogDescription =
    descriptionOverride ??
    (mode === 'edit'
      ? 'Update your webhook endpoint. Your server must return the verify token on a GET request and accept POST requests.'
      : 'Set up your webhook endpoint. Your server must return the verify token on a GET request and accept POST requests.');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            {dialogTitle}
          </DialogTitle>
          <DialogDescription>{dialogDescription}</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <form.Field
              name="webhookUrl"
              validators={{
                onChange: ({ value }) => validateWebhookUrl(value),
                onSubmit: ({ value }) => validateWebhookUrl(value),
              }}
            >
              {(field) => {
                const hasError = field.state.meta.errors.length > 0;
                return (
                  <>
                    <Label htmlFor="webhook-url">Webhook URL</Label>
                    <Input
                      id="webhook-url"
                      value={field.state.value}
                      onChange={(e) => {
                        if (error) setError(null);
                        field.handleChange(e.target.value);
                      }}
                      onBlur={field.handleBlur}
                      placeholder="https://your-server.com/webhook"
                      disabled={verifying}
                      autoFocus
                      aria-invalid={hasError || undefined}
                      aria-describedby={hasError ? 'webhook-url-error' : undefined}
                    />
                    <FieldError id="webhook-url-error" errors={field.state.meta.errors} />
                  </>
                );
              }}
            </form.Field>
            {error && (
              <div className="mt-2 space-y-1">
                <p className="text-destructive text-sm">{error}</p>
                <a
                  href={docsUrl('platform/webhook-routing')}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline"
                >
                  Learn more about webhook setup
                  <ExternalLink className="size-3" />
                </a>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="verify-token">Verify Token</Label>
            <Input
              id="verify-token"
              value={verifyToken}
              onChange={(e) => setVerifyToken(e.target.value.trim())}
              disabled={verifying}
            />
            <p className="text-muted-foreground text-xs">
              {verifyTokenHelp ?? (
                <>
                  Your server must return this token in the body of a GET request.
                  {mode !== 'edit' && ' We generated one for you, or enter your own.'}
                </>
              )}
            </p>
          </div>

          <form.Subscribe selector={(state) => state.values.webhookUrl}>
            {(webhookUrl) => (
              <Button
                className="w-full"
                onClick={() => void form.handleSubmit()}
                disabled={!webhookUrl.trim() || verifying}
              >
                {verifying && <Loader2 className="size-4 animate-spin" />}
                {verifying ? 'Verifying...' : ctaText}
              </Button>
            )}
          </form.Subscribe>
        </div>
      </DialogContent>
    </Dialog>
  );
}
